import React from 'react';

import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useDeleteMembershipGroup } from 'src/features/memberships/api/useDeleteMembershipGroup';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { MembershipGroup } from 'src/features/memberships/types';
import type { DataTableNewEntryProps } from 'src/registry/components/table/types';

interface MembershipGroupDeleteFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMembershipGroup?: MembershipGroup;
    children?: React.ReactNode;
}

function MembershipGroupDeleteFormModal<TData>({
    selectedMembershipGroup,
    children,
}: MembershipGroupDeleteFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const navigate = useNavigate();

    const onOpenChange = (isOpen: boolean) => {
        if (setIsModalOpen) {
            setIsModalOpen(isOpen);
        }
    };

    const { mutate: deleteMembershipGroup } = useDeleteMembershipGroup({
        onSuccess: () => {
            void navigate(`/vehicles/memberships`);
            toast({
                title: formatMessage(membershipMessages.membershipDeleted),
                description: formatMessage(membershipMessages.membershipDeletedSuccessfully, {
                    name: selectedMembershipGroup?.name,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data?.all;
            // FORBIDEN is misspelled on purpose
            const hasForbiddenCode = errorData && errorData.some((err) => err.code === 'FORBIDEN');
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = hasForbiddenCode
                ? formatMessage(membershipMessages.membershipDeleteError, { name: selectedMembershipGroup?.name })
                : formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-2">
                        <SquareIcon icon={faTrashAlt} />
                        <div>
                            <DialogTitle>{formatMessage(membershipMessages.deleteMembership)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(membershipMessages.deleteMembershipName, {
                                    name: selectedMembershipGroup?.name,
                                })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        {formatMessage(membershipMessages.confirmDeleteMembership, {
                            name: selectedMembershipGroup?.name,
                        })}
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        onClick={() => {
                            if (selectedMembershipGroup) {
                                void deleteMembershipGroup({
                                    parkingId,
                                    membershipGroupId: selectedMembershipGroup.id,
                                });
                                setIsModalOpen(!isModalOpen);
                            }
                        }}
                        className="flex-1"
                    >
                        {formatMessage(membershipMessages.deleteMembership)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { MembershipGroupDeleteFormModal };
